import {studentGridThemeColors, studentThemeContrastColors} from '@styles/themes';
import {
    DetailsContainer,
    DetailsLine,
    DetailsTitleBottom,
    DetailsTitleTop,
    GridCardContainer,
    GridCardStudentInfo,
    StudentImage,
} from './GridCard.styled';
import {useStore} from '@store/appStore';
import {memo, MouseEventHandler, useEffect} from 'react';
import { shallow } from 'zustand/shallow';
import {AttendanceMap, AttendanceStatus} from '@models/AttendanceType';
import persistentStore from '@store/persistentStore';
import {animated} from 'react-spring';
import moment from 'moment';
import useVisibility from '@hooks/useVisibility';
import StudentThumbnail from '@components/StudentThumbnail';
import {useSearch} from '@providers/SearchProvider';
import SkeletonAnim from '@components/Animation/SkeletonAnim';
import PlaceholderPerson from '@components/PlaceholderPerson';
import {Shadow} from '@styles/reusable';
import GridCardStats from '../GridCardStats';
import {ViewColoring, ViewIndicators} from '@models/Accessibility';
import AttendanceIcon from '../../AttendanceIcon';
import HaveNote from '@components/Buttons/HaveNote';
import RegMethod from "@components/Buttons/RegistrationMethod";
import { calculatePercentage, ShowabsenceOrAttendance } from '@utils/percentageTools';
import StatusBar from '@components/Buttons/StatusBar';
import { normalizeName } from '@utils/textTools';
import Tooltip from '@components/Tooltips/Tooltip';

interface Props {
  content: any;
  onClick: MouseEventHandler<HTMLDivElement>;
  index: number;
  style?: any;
}

const AnimatedGridCardContainer = animated(GridCardContainer);

export const GridCard = ({ content, onClick, style, index }: Props) => {
  const { username, firstname, uni_name, sis_id, lastname, displayname, type, total_percentage, loading, course_percentage, id, notePresent, noteUnreadCount, initial_Method, duration, course_absence_duration, course_absence_lessons, course_duration, course_lessons, total_absence_duration, total_absence_lessons, total_duration,total_lessons, merit, note, tag_ids } = content;
  const { setRegistrationIndex, registrationIndex, settings, schedule, scheduleIndex, systemSettings } = useStore(
    (state) => ({
      setRegistrationIndex: state.setRegistrationIndex,
      registrationIndex: state.registrationIndex,
      schedule: state.schedule,
      scheduleIndex: state.scheduleIndex,
      settings: state.settings,
      systemSettings: state.systemSettings
    }),
    shallow,
  );
  const { registrationQuery } = useSearch();
  const { privacyEnabled, viewColoring, viewIndicators } = persistentStore();
  const isInFuture = moment(registrationQuery.date).isAfter(moment());
  const isSelected = registrationIndex === index;
  const [isVisible, ref] = useVisibility<HTMLDivElement>(-30);
  const currentSchedule = schedule.data ? schedule.data[scheduleIndex] : { name: '' };
  const name = firstname ? firstname : displayname ? displayname : '';
  const combinedSchedule = currentSchedule?.name?.toString()?.includes(' + ');
  let defaultAttendanceSetting = systemSettings.find(s => s.APP_Setting_ID === 19)
  const attendanceStatus: string = AttendanceMap[type] || 'default';
  const status = defaultAttendanceSetting && defaultAttendanceSetting.value === "Present" && type === -1?AttendanceMap[1]:attendanceStatus
  const coloring = viewColoring === ViewColoring.CONTRAST ? studentThemeContrastColors[status] : studentGridThemeColors[status] ;

  const scrollWinTo = () => {
    const element = ref.current;
    const offset = 170;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  console.log(initial_Method)

  useEffect(() => {
    if (registrationIndex === 0) {
      return;
    }
    // console.log(isSelected);
    if (!isVisible && isSelected) {
      scrollWinTo();
    }
  }, [registrationIndex, isSelected]);

  useEffect(() => {
    if (registrationQuery.forType === 'STUDENT') {
      if (username !== registrationQuery.forVal) {
        return;
      }
      setRegistrationIndex(index);
      // scrollWinTo();
    }
  }, [registrationQuery]);

  const handleClick = (e) => {
    if (registrationQuery.forType === 'STUDENT') {
      if (username !== registrationQuery.forVal) {
        e.preventDefault();
        return;
      }
    }
    onClick(sis_id);
  };

  return (
    <AnimatedGridCardContainer
      style={style}
      selected={isSelected}
      onClick={onClick ? (e) => handleClick(e) : null}
      // @ts-ignore
      css={coloring}
      ref={ref}
      blurred={registrationQuery.forType === 'STUDENT' && registrationQuery.forVal !== username}
    >
      <StudentImage>
        <StudentThumbnail regView={true} top={"-18%"} font={"3em"} name={firstname + " " + (lastname && lastname)} src={username} />
        {viewIndicators === ViewIndicators.COLOR? "" : <AttendanceIcon midPos={true} attendanceStatus={status} isContrast={viewColoring === ViewColoring.CONTRAST || viewIndicators === ViewIndicators.COLOR_AND_CONTRAST_ICONS} />}
      </StudentImage>
      <GridCardStudentInfo backgroundColor={viewIndicators === ViewIndicators.ICONS}>
        <DetailsContainer contrast={viewColoring === ViewColoring.CONTRAST || viewIndicators === ViewIndicators.ICONS} attendanceStatus={status}>
          <DetailsLine>
            <DetailsTitleTop>{firstname.length > 12?<Tooltip content={firstname}>{normalizeName(firstname, 12)}</Tooltip>:firstname}</DetailsTitleTop>
            <GridCardStats
              small={false}
              loading={loading}
              stat={ShowabsenceOrAttendance(settings.SHOWPRESENCE, merit?calculatePercentage(course_duration, course_absence_duration):course_percentage)}
              noPrivacy={!privacyEnabled && settings?.ENABLE_PRIVACY_SETTING === 'true'}
              isInFuture={isInFuture}
            />
          </DetailsLine>
          <DetailsLine>
            <DetailsTitleBottom>{lastname.length > 14?<Tooltip content={lastname}>{normalizeName(lastname, 14)}</Tooltip>:lastname}</DetailsTitleBottom>
            {!combinedSchedule && (
              <GridCardStats
                small={true}
                loading={loading}
                stat={ShowabsenceOrAttendance(settings.SHOWPRESENCE, merit?calculatePercentage(total_duration, total_absence_duration):total_percentage)}
                noPrivacy={!privacyEnabled && settings?.ENABLE_PRIVACY_SETTING === 'true'}
                isInFuture={isInFuture}
              />
            )}
          </DetailsLine>
        </DetailsContainer>
      </GridCardStudentInfo>
      <Shadow size={3} />
      <StatusBar note={{interact:(registrationQuery.forType === 'STUDENT' && registrationQuery.forVal !== username), id: sis_id, show:notePresent, notecount:noteUnreadCount}} reason={{type: type, note:note, tag_ids: tag_ids}}>
        <RegMethod studentUsername={username} studentId={sis_id} reservationId={schedule?.data[scheduleIndex]?.id} show={initial_Method !== null && initial_Method !== 0} registrationMethod={initial_Method} />
      </StatusBar>
    </AnimatedGridCardContainer>
  );
};

export const MockGridCard = () => {
  return (
    <GridCardContainer selected={false} blurred={false} mock={true}>
      <StudentImage>
        <PlaceholderPerson />
      </StudentImage>
      <GridCardStudentInfo mock={true}>
        <DetailsContainer attendanceStatus={AttendanceStatus.Unknown}>
          <h3>{/* <SkeletonAnim width={60} height={15} /> */}</h3>
          <div>
            <span>
              <SkeletonAnim width={100} height={15} />
            </span>
          </div>
        </DetailsContainer>
      </GridCardStudentInfo>
    </GridCardContainer>
  );
};

export default memo(GridCard);
